<template>
  <v-dialog
    :value="show"
    max-width="880px"
    persistent
    @input="onDialogInput"
  >
    <div class="wa-template-dialog-shell">
      <v-card class="wa-template-modal">
      <v-toolbar flat class="k-orange-linear wa-template-modal-toolbar">
        <v-toolbar-title class="white--text pl-2 py-2">
          {{ step === 1 ? 'Plantillas de WhatsApp' : (selectedTemplate && selectedTemplate.name) || 'Plantilla' }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :disabled="sending" @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text v-if="step === 1" class="pt-3 wa-template-modal-body wa-template-step1-body">
        <v-text-field
          v-model="search"
          solo
          prepend-icon="fa-search"
          label="Buscar por nombre..."
          clearable
          hide-details
          class="mb-2"
          :disabled="sending"
        />
        <v-progress-linear v-if="loading" indeterminate color="k-orange-linear" class="my-2" />
        <div v-else-if="!templates.length" class="py-4 text-xs-center grey--text">
          No hay plantillas que coincidan con la búsqueda o no tenés permiso para verlas.
        </div>
        <v-list v-else two-line class="py-0 wa-template-list">
          <v-list-tile
            v-for="(item, index) in templates"
            :key="item.id"
            avatar
            class="wa-template-tile"
            :class="{ 'wa-template-tile--bordered': index < templates.length - 1 }"
            :disabled="sending"
            @click="selectTemplate(item)"
          >
            <v-list-tile-avatar>
              <v-avatar color="green" size="40">
                <v-icon color="white" small>fab fa-whatsapp</v-icon>
              </v-avatar>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-bold">
                {{ item.name }}
              </v-list-tile-title>
              <v-list-tile-sub-title class="text-truncate-two">
                {{ previewSnippet(item) }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-btn flat class="k-orange-linear-text" :disabled="sending" @click.stop="selectTemplate(item)">
                Usar
              </v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
      </v-card-text>

      <v-card-text
        v-else
        class="pt-3 wa-template-modal-body wa-template-step2-body"
      >
        <div class="wa-template-step2-inner">
          <v-layout row wrap class="wa-template-customer-summary mb-2">
            <v-flex xs4 class="wa-template-customer-summary__col">
              <div class="wa-template-customer-summary__label">Nombre</div>
              <div class="wa-template-customer-summary__value">{{ summaryChat | getName }}</div>
            </v-flex>
            <v-flex xs4 class="wa-template-customer-summary__col">
              <div class="wa-template-customer-summary__label">Cuenta</div>
              <div class="wa-template-customer-summary__value">{{ customerAccountLabel }}</div>
            </v-flex>
            <v-flex xs4 class="wa-template-customer-summary__col">
              <div class="wa-template-customer-summary__label">RUT/DNI/CI</div>
              <div class="wa-template-customer-summary__value">{{ customerFiscalIdLabel }}</div>
            </v-flex>
          </v-layout>
          <div class="subheading mb-2 grey--text text--darken-1">
            Vista previa del mensaje
          </div>
          <div class="template-preview-box">
            <span
              v-for="seg in previewSegments"
              :key="seg.key"
            >{{ seg.text }}</span>
          </div>

          <template v-if="templateVariables.length">
            <v-divider class="wa-template-step2-divider" />
            <div class="wa-template-vars-heading mb-2 grey--text text--darken-1">
              <v-icon small class="grey--text wa-template-vars-heading__icon">fas fa-edit</v-icon>
              <span class="wa-template-vars-heading__label">Variables de la plantilla</span>
            </div>
            <v-layout
              v-for="name in templateVariables"
              :key="`var-${name}`"
              row
              wrap
              align-center
              class="mb-2 wa-template-var-row"
            >
              <v-flex xs5 pr-1>
                <v-text-field
                  :value="name"
                  solo
                  readonly
                  hide-details
                  prepend-inner-icon="fas fa-tag"
                  class="elevation-0 wa-template-var-field wa-template-var-field--name"
                />
              </v-flex>
              <v-flex xs1 class="text-xs-center">
                <v-icon small class="grey--text">fas fa-arrow-right</v-icon>
              </v-flex>
              <v-flex xs6 pl-1>
                <v-text-field
                  v-model="variableValues[name]"
                  solo
                  hide-details
                  :label="`Valor para @${name}`"
                  class="elevation-0 wa-template-var-field wa-template-var-field--value"
                  :disabled="sending"
                />
              </v-flex>
            </v-layout>
          </template>
        </div>
      </v-card-text>

      <v-card-actions class="px-3 pb-3 wa-template-modal-actions">
        <v-btn v-if="step === 2" flat :disabled="sending" @click="goBack">
          <v-icon left small>fas fa-arrow-left</v-icon>
          Atrás
        </v-btn>
        <v-spacer />
        <v-btn flat :disabled="sending" @click="close">
          Cerrar
        </v-btn>
        <v-btn
          v-if="step === 2"
          class="k-orange-linear white--text"
          :disabled="!allVariablesFilled || sending"
          :loading="sending"
          @click="submitTemplate"
        >
          <v-icon left small>fas fa-paper-plane</v-icon>
          Enviar mensaje
        </v-btn>
      </v-card-actions>
    </v-card>
    </div>
  </v-dialog>
</template>

<script>
import _ from 'lodash';
import EventBus from '@/event-bus';
import chatCenterMixin from '@/mixins/chatCenterMixin';

/**
 * Parsea `text` de whatsapp_templates: JSON con `value` y `custom_parameters`.
 */
export function parseWhatsappTemplateBody(rawText) {
  if (rawText == null || rawText === '') {
    return { value: '', custom_parameters: [] };
  }
  if (typeof rawText === 'object' && rawText !== null && typeof rawText.value === 'string') {
    return {
      value: rawText.value,
      custom_parameters: Array.isArray(rawText.custom_parameters) ? rawText.custom_parameters : [],
    };
  }
  try {
    const obj = JSON.parse(rawText);
    if (obj && typeof obj.value === 'string') {
      return {
        value: obj.value,
        custom_parameters: Array.isArray(obj.custom_parameters) ? obj.custom_parameters : [],
      };
    }
  } catch (e) {
    /* ignore */
  }
  return { value: String(rawText), custom_parameters: [] };
}

export function applyAtParameters(valueStr, valuesByName) {
  if (!valueStr) {
    return '';
  }
  let out = valueStr;
  const names = Object.keys(valuesByName || {});
  names.sort((a, b) => b.length - a.length);
  names.forEach((name) => {
    const v = valuesByName[name];
    if (v === undefined || v === null || String(v).trim() === '') {
      return;
    }
    const token = `@${name}`;
    const parts = out.split(token);
    out = parts.join(v);
  });
  return out;
}

/**
 * Segmentos para vista previa: texto sustituido si hay valor; @param resaltado si aún vacío.
 */
export function buildPreviewSegments(valueStr, customParameters, variableValues) {
  const paramSet = new Set((customParameters || []).filter(Boolean));
  if (!valueStr) {
    return [{ type: 'text', text: '', key: 'empty' }];
  }
  const re = /@([a-zA-Z0-9_]+)/g;
  const segments = [];
  let lastIndex = 0;
  let m = re.exec(valueStr);
  while (m !== null) {
    if (m.index > lastIndex) {
      segments.push({
        type: 'text',
        text: valueStr.slice(lastIndex, m.index),
        key: `k-${segments.length}`,
      });
    }
    const name = m[1];
    const token = m[0];
    if (paramSet.has(name)) {
      const val = variableValues[name];
      if (val != null && String(val).trim() !== '') {
        segments.push({ type: 'text', text: String(val), key: `k-${segments.length}` });
      } else {
        segments.push({ type: 'placeholder', text: token, key: `k-${segments.length}` });
      }
    } else {
      segments.push({ type: 'text', text: token, key: `k-${segments.length}` });
    }
    lastIndex = m.index + token.length;
    m = re.exec(valueStr);
  }
  if (lastIndex < valueStr.length) {
    segments.push({ type: 'text', text: valueStr.slice(lastIndex), key: `k-${segments.length}` });
  }
  return segments.length ? segments : [{ type: 'text', text: valueStr, key: 't-0' }];
}

export default {
  name: 'WhatsappTemplatesChatModal',
  mixins: [chatCenterMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    sending: {
      type: Boolean,
      default: false,
    },
    chat: {
      type: Object,
      default: () => ({}),
    },
    hasCustomer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 1,
      search: '',
      loading: false,
      templates: [],
      selectedTemplate: null,
      parsedBody: { value: '', custom_parameters: [] },
      variableValues: {},
      fetchTemplatesCancelSource: null,
      customerDetail: null,
      loadingCustomerDetail: false,
    };
  },
  created() {
    this.debouncedFetchTemplates = _.debounce(() => {
      if (this.show && this.step === 1) {
        this.fetchTemplates();
      }
    }, 400);
  },
  destroyed() {
    if (this.fetchTemplatesCancelSource) {
      this.fetchTemplatesCancelSource.cancel('component destroyed');
    }
  },
  computed: {
    customerForSummary() {
      if (this.customerDetail) {
        return this.customerDetail;
      }
      return (this.chat && this.chat.customer) || {};
    },
    summaryChat() {
      return {
        ...(this.chat || {}),
        customer: this.customerForSummary,
      };
    },
    customerAccountLabel() {
      const account = this.customerForSummary.account;
      return account != null && String(account).trim() !== '' ? String(account).trim() : 'Sin especificar';
    },
    customerFiscalIdLabel() {
      const fiscalId = this.customerForSummary.fiscal_id;
      return fiscalId != null && String(fiscalId).trim() !== '' ? String(fiscalId).trim() : 'Sin especificar';
    },
    templateVariables() {
      const list = this.parsedBody.custom_parameters || [];
      return list.filter((n, i) => n && list.indexOf(n) === i);
    },
    previewResolved() {
      return applyAtParameters(this.parsedBody.value, this.variableValues);
    },
    previewSegments() {
      return buildPreviewSegments(
        this.parsedBody.value,
        this.parsedBody.custom_parameters || [],
        this.variableValues,
      );
    },
    allVariablesFilled() {
      const vars = this.templateVariables;
      if (!vars.length) {
        return true;
      }
      return vars.every((name) => {
        const v = this.variableValues[name];
        return v != null && String(v).trim() !== '';
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.reset();
        this.fetchTemplates();
        this.loadCustomerDetail();
      } else if (this.fetchTemplatesCancelSource) {
        this.fetchTemplatesCancelSource.cancel('modal closed');
      }
    },
    'chat.customer.id'(id) {
      if (this.show && id) {
        this.loadCustomerDetail();
      }
    },
    'chat.customer': {
      deep: true,
      handler(customer) {
        if (!this.show || !customer || !customer.id) {
          return;
        }
        const hasAccount = customer.account != null && String(customer.account).trim() !== '';
        const hasFiscal = customer.fiscal_id != null && String(customer.fiscal_id).trim() !== '';
        if (hasAccount && hasFiscal) {
          this.customerDetail = customer;
        }
      },
    },
    search() {
      if (!this.show || this.step !== 1) {
        return;
      }
      this.debouncedFetchTemplates();
    },
  },
  methods: {
    onDialogInput(val) {
      if (!val && !this.sending) {
        this.$emit('update:show', false);
      }
    },
    previewSnippet(item) {
      const { value } = parseWhatsappTemplateBody(item.text);
      const line = (value || '').replace(/\s+/g, ' ').trim();
      return line.length > 120 ? `${line.slice(0, 117)}…` : line;
    },
    async fetchTemplates() {
      if (this.fetchTemplatesCancelSource) {
        this.fetchTemplatesCancelSource.cancel('new request');
      }
      const source = this.axios.CancelToken.source();
      this.fetchTemplatesCancelSource = source;
      this.loading = true;
      this.templates = [];
      try {
        const q = (this.search || '').trim();
        const params = { paginate: 0 };
        if (q) {
          params.search = q;
        }
        const { data } = await this.axios.get('whatsapp_templates', {
          params,
          cancelToken: source.token,
        });
        if (this.fetchTemplatesCancelSource !== source) {
          return;
        }
        const raw = data;
        const list = Array.isArray(raw)
          ? raw
          : (raw && Array.isArray(raw.data) ? raw.data : []);
        this.templates = list;
      } catch (e) {
        if (this.axios.isCancel(e)) {
          return;
        }
        if (this.fetchTemplatesCancelSource !== source) {
          return;
        }
        this.templates = [];
        EventBus.$emit('modal', 'No se pudieron cargar las plantillas de WhatsApp. Verificá permisos o intentá más tarde.');
      } finally {
        if (this.fetchTemplatesCancelSource === source) {
          this.loading = false;
        }
      }
    },
    selectTemplate(item) {
      if (this.sending) {
        return;
      }
      this.selectedTemplate = item;
      this.parsedBody = parseWhatsappTemplateBody(item.text);
      const next = {};
      (this.parsedBody.custom_parameters || []).forEach((name) => {
        if (name) {
          next[name] = this.variableValues[name] || '';
        }
      });
      this.variableValues = next;
      this.step = 2;
      this.loadCustomerDetail();
    },
    goBack() {
      if (this.sending) {
        return;
      }
      this.step = 1;
      this.selectedTemplate = null;
      this.parsedBody = { value: '', custom_parameters: [] };
      this.variableValues = {};
    },
    reset() {
      this.step = 1;
      this.search = '';
      this.selectedTemplate = null;
      this.parsedBody = { value: '', custom_parameters: [] };
      this.variableValues = {};
      this.customerDetail = null;
      this.loadingCustomerDetail = false;
    },
    loadCustomerDetail() {
      const customer = this.chat && this.chat.customer;
      const customerId = customer && customer.id;
      if (!customerId) {
        this.customerDetail = null;
        return;
      }
      const hasAccount = customer.account != null && String(customer.account).trim() !== '';
      const hasFiscal = customer.fiscal_id != null && String(customer.fiscal_id).trim() !== '';
      if (hasAccount && hasFiscal) {
        this.customerDetail = customer;
        return;
      }
      this.loadingCustomerDetail = true;
      this.axios.get(`customers/${customerId}/chat_center`)
        .then(({ data }) => {
          this.customerDetail = data;
        })
        .catch(() => {
          this.customerDetail = customer;
        })
        .finally(() => {
          this.loadingCustomerDetail = false;
        });
    },
    close() {
      if (this.sending) {
        return;
      }
      this.$emit('update:show', false);
    },
    submitTemplate() {
      if (this.sending || !this.allVariablesFilled) {
        return;
      }
      const payload = {
        whatsapp_template_id: this.selectedTemplate && this.selectedTemplate.id,
        resolved_text: this.previewResolved,
        variable_values: { ...this.variableValues },
      };
      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
.text-truncate-two {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.wa-template-tile:hover {
  background-color: #fff3f0 !important;
}

.wa-template-customer-summary {
  font-size: 13px;
  line-height: 1.45;
}

.wa-template-customer-summary__col {
  min-width: 0;
  margin: 10px 0 10px 0;
}

.wa-template-customer-summary__label {
  font-size: 12px;
  font-weight: 500;
  color: #757575;
  margin-bottom: 2px;
}

.wa-template-customer-summary__value {
  color: #424242;
  word-break: break-word;
}

.template-preview-box {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 15px;
  line-height: 1.65;
  color: #333;
  min-height: 120px;
  height: 180px;
  max-height: 28vh;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 8px;
  word-break: break-word;
  white-space: pre-wrap;
}

.wa-template-step2-divider {
  margin-top: 6px !important;
  margin-bottom: 6px !important;
  opacity: 0.85;
}

.wa-template-vars-heading {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
}

.wa-template-vars-heading__icon {
  flex: 0 0 auto;
  margin-right: 6px !important;
  margin-left: 0 !important;
}

.wa-template-vars-heading__label {
  flex: 0 1 auto;
}

/* Fondo gris (scoped + hijos internos del input-group) */
.wa-template-var-field >>> .input-group__input {
  background-color: #eeeeee !important;
  border-radius: 6px !important;
}

.wa-template-var-field >>> .input-group {
  background-color: #eeeeee !important;
  border-radius: 6px !important;
}

.wa-template-var-field >>> .input-group--focused .input-group__input {
  background-color: #e8e8e8 !important;
}

.wa-template-var-field >>> .input-group__details {
  display: none;
}

.wa-template-tile--bordered {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.wa-template-list {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}
</style>

<style>
/*
 * Contenedor con alto fijo: el v-dialog anidado dentro del ChatRoom a veces no deja que la v-card
 * tome 60vh solo con estilos scoped. Este shell (clase única) fuerza el alto real.
 */
.wa-template-dialog-shell {
  height: 70vh;
  min-height: 70vh;
  max-height: 70vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.wa-template-dialog-shell .wa-template-modal {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  width: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.wa-template-dialog-shell .wa-template-modal nav.toolbar,
.wa-template-dialog-shell .wa-template-modal .toolbar {
  flex: 0 0 auto;
}

.wa-template-dialog-shell .wa-template-modal .card__text {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.wa-template-dialog-shell .wa-template-modal .card__text.wa-template-step2-body {
  display: block;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.wa-template-dialog-shell .wa-template-modal .card__actions {
  flex: 0 0 auto;
}

.wa-template-dialog-shell .wa-template-modal .wa-template-step1-body > .input-group,
.wa-template-dialog-shell .wa-template-modal .wa-template-step1-body > .progress-linear,
.wa-template-dialog-shell .wa-template-modal .wa-template-step1-body > div:not(.wa-template-list) {
  flex: 0 0 auto;
}

.wa-template-dialog-shell .wa-template-modal .wa-template-step1-body .wa-template-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* Paso 2: título + icono pegados a la izquierda; inputs variables con gris (sin depender solo de scoped) */
.wa-template-dialog-shell .wa-template-vars-heading {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.wa-template-dialog-shell .wa-template-vars-heading__icon {
  margin-right: 6px !important;
}

.wa-template-dialog-shell .input-group.wa-template-var-field,
.wa-template-dialog-shell .input-group.wa-template-var-field .input-group__input {
  background-color: #eeeeee !important;
  border-radius: 6px !important;
}

.wa-template-dialog-shell .input-group.wa-template-var-field.input-group--focused .input-group__input {
  background-color: #e8e8e8 !important;
}

.wa-template-dialog-shell .input-group.wa-template-var-field input,
.wa-template-dialog-shell .input-group.wa-template-var-field textarea {
  background-color: transparent !important;
}
</style>



// WEBPACK FOOTER //
// src/components/chat_center_v2/WhatsappTemplatesChatModal.vue